/**
 * Catalogue de chaînes FR (SITE_SPEC §7 : « French everywhere »).
 *
 * Pas de sélecteur de langue — le site est FR en dur. Ce module centralise les
 * libellés récurrents et fournit des helpers de formatage `fr-FR` (dates, nombres).
 * Les pages Cores importent `fr` et les helpers ; les libellés spécifiques (raretés,
 * stats, métriques de classement, catégories wiki) vivent dans `@etheria/shared`.
 */

export const fr = {
  brand: {
    name: "Etheria",
    tagline: "La Conscience de Jeawyn",
    full: "Etheria — Serveur MMORPG dark-fantasy",
  },

  nav: {
    accueil: "Accueil",
    vote: "Vote",
    boutique: "Boutique",
    carte: "Carte",
    classements: "Classements",
    monde: "Monde Vivant",
    statut: "Statut",
    wiki: "Wiki",
    guildes: "Guildes",
    joueurs: "Joueurs",
    forum: "Forum",
    signalements: "Signalements",
    suggestions: "Suggestions",
    rng: "Système RNG",
    armurerie: "Armurerie",
    patchnotes: "Patchnotes",
    // Groupes / menus déroulants
    jouer: "Jouer",
    communaute: "Communauté",
    encyclopedie: "Encyclopédie",
    panel: "Mon espace",
    admin: "Administration",
  },

  /** Descriptions courtes affichées sous les libellés dans les menus déroulants. */
  navDesc: {
    accueil: "Le portail du royaume",
    vote: "Soutiens le serveur, gagne des récompenses",
    boutique: "Grades, cosmétiques et tokens",
    statut: "État du serveur en temps réel",
    wiki: "L'encyclopédie complète d'Etheria",
    metiers: "25 métiers et leurs collections",
    items: "Armes, armures et raretés",
    enchants: "Grimoire des enchantements",
    donjons: "Instances, vagues et butins",
    guide: "Comprendre toutes les mécaniques",
    classements: "Les meilleurs aventuriers",
    carte: "Carte 3D vivante du monde",
    monde: "Chroniques du Monde Vivant",
    guildes: "Ordres et alliances de joueurs",
    forum: "La taverne du royaume : débats et entraide",
    signalements: "Signaler un bug ou un joueur au staff",
    suggestions: "Proposez et votez les idées du serveur",
    rng: "Le destin des butins : probabilités & calculateur",
    armurerie: "La vitrine publique de chaque aventurier",
    patchnotes: "Toutes les mises à jour visibles pour les joueurs",
  },

  panelNav: {
    dashboard: "Tableau de bord",
    battlepass: "BattlePass",
    quetes: "Quêtes quotidiennes",
    profil: "Profil",
    inventaire: "Inventaire",
    statistiques: "Statistiques",
    historique: "Historique",
    economie: "Économie",
    claims: "Mes Claims",
    homes: "Mes Homes",
    guilde: "Ma Guilde",
    hdv: "Hôtel des ventes",
    parametres: "Paramètres",
    liaison: "Liaison du compte",
  },

  adminNav: {
    dashboard: "Tableau de bord",
    historiqueMaster: "Historique Master",
    joueurs: "Joueurs",
    grades: "Grades",
    economie: "Économie",
    npcs: "PNJ serveur",
    items: "Items / Give",
    mobs: "Mobs",
    config: "Config plugins",
    deploiement: "Déploiement",
    contenu: "Contenu du site",
    forum: "Forum",
    signalements: "Signalements",
    suggestions: "Suggestions",
    moderation: "Modération",
    broadcast: "Broadcast",
    perf: "Perf / TPS",
    logs: "Logs d'audit",
    carte: "Carte (admin)",
    reglages: "Réglages",
  },

  auth: {
    connexion: "Connexion",
    inscription: "Inscription",
    deconnexion: "Déconnexion",
    motDePasseOublie: "Mot de passe oublié",
    identifiant: "Email ou pseudo",
    email: "Adresse email",
    motDePasse: "Mot de passe",
    confirmerMotDePasse: "Confirmer le mot de passe",
    seSouvenir: "Se souvenir de moi",
    pasDeCompte: "Pas encore de compte ?",
    dejaCompte: "Déjà un compte ?",
    codeLiaison: "Code de liaison (6 caractères)",
    lierCompte: "Lier mon compte Minecraft",
    nonConnecte: "Non connecté",
  },

  common: {
    chargement: "Chargement…",
    aucunResultat: "Aucun résultat",
    aucuneDonnee: "Aucune donnée disponible",
    erreur: "Une erreur est survenue",
    reessayer: "Réessayer",
    rechercher: "Rechercher…",
    filtrer: "Filtrer",
    voirPlus: "Voir plus",
    voirTout: "Voir tout",
    retour: "Retour",
    precedent: "Précédent",
    suivant: "Suivant",
    page: "Page",
    sur: "sur",
    fermer: "Fermer",
    annuler: "Annuler",
    confirmer: "Confirmer",
    enregistrer: "Enregistrer",
    copie: "Copié !",
    enLigne: "En ligne",
    horsLigne: "Hors ligne",
    niveau: "Niveau",
    rang: "Rang",
    classe: "Classe",
    guilde: "Guilde",
    solde: "Solde",
  },

  home: {
    heroKicker: "Serveur survie MMORPG · Monde vivant",
    heroTitle: "Etheria ouvre ses portes",
    heroSubtitle:
      "Prépare tes sorties, suis ta progression et retrouve le royaume en direct : carte live, armurerie, métiers, donjons, guildes et patchnotes restent au même endroit.",
    ctaJouer: "Rejoindre le serveur",
    ctaDiscord: "Rejoindre le Discord",
    ctaWiki: "Explorer le wiki",
    joueursEnLigne: "Joueurs en ligne",
    statutServeur: "Statut du serveur",
    chroniquesTitre: "Chroniques du Monde Vivant",
    chroniquesSubtitle: "Le récit quotidien tissé par les actes des joueurs",
    apercuCarte: "Aperçu de la carte",
    voirCarte: "Ouvrir la carte live",
    ipCopier: "Copier l'IP",
    ipServeur: "Adresse du serveur",
    copier: "Copier",
    rejoindreAventure: "Rejoins l'aventure",
    tps: "TPS",
    version: "Version",
  },

  footer: {
    rejoindre: "Rejoindre",
    encyclopedie: "Encyclopédie",
    communaute: "Communauté",
    legal: "Mentions & confidentialité",
    droits: "Tous droits réservés.",
    fanContent:
      "Etheria n'est pas affilié à Mojang ou Microsoft. « Minecraft » est une marque de Mojang Synergies AB.",
  },

  errors: {
    pageIntrouvable: "Page introuvable",
    pageIntrouvableDesc: "Ce chemin s'est perdu dans la brume d'Etheria.",
    accesRefuse: "Accès refusé",
    accesRefuseDesc: "Vous n'avez pas les droits nécessaires pour cette section.",
    connexionRequise: "Connexion requise",
    connexionRequiseDesc: "Connectez-vous pour accéder à votre espace.",
    retourAccueil: "Retour à l'accueil",
  },
} as const;

// ------------------------------------------------------------------ //
// Helpers de formatage fr-FR
// ------------------------------------------------------------------ //

const nfInt = new Intl.NumberFormat("fr-FR");
const nfDec = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 });

/** Nombre entier « 1 234 567 ». */
export function formatNumber(n: number): string {
  return nfInt.format(Math.round(n));
}

/** Nombre décimal « 1 234,57 ». */
export function formatDecimal(n: number): string {
  return nfDec.format(n);
}

/** Solde BigDecimal (chaîne) « 1 234 567,89 pièces ». */
export function formatMoney(value: string, suffix = "pièces"): string {
  const neg = value.startsWith("-");
  const raw = neg ? value.slice(1) : value;
  const [intPart = "0", decPart] = raw.split(".");
  const intFmt = nfInt.format(BigInt(intPart));
  const dec = decPart ? "," + decPart.slice(0, 2).padEnd(2, "0") : "";
  return `${neg ? "-" : ""}${intFmt}${dec} ${suffix}`;
}

/** Durée depuis des secondes « 12 j 4 h 30 min ». */
export function formatDuration(seconds: number): string {
  if (seconds <= 0) return "0 min";
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const parts: string[] = [];
  if (d) parts.push(`${d} j`);
  if (h) parts.push(`${h} h`);
  if (m && !d) parts.push(`${m} min`);
  return parts.slice(0, 2).join(" ") || "< 1 min";
}

const relFmt = new Intl.RelativeTimeFormat("fr-FR", { numeric: "auto" });

/** Temps relatif « il y a 3 heures » à partir d'un epoch ms. */
export function formatRelative(epochMs: number): string {
  const diff = epochMs - Date.now();
  const abs = Math.abs(diff);
  const min = 60_000, hour = 3_600_000, day = 86_400_000;
  if (abs < hour) return relFmt.format(Math.round(diff / min), "minute");
  if (abs < day) return relFmt.format(Math.round(diff / hour), "hour");
  return relFmt.format(Math.round(diff / day), "day");
}

const dateFmt = new Intl.DateTimeFormat("fr-FR", {
  dateStyle: "medium",
  timeStyle: "short",
});

/** Date+heure « 1 juil. 2026, 20:00 ». */
export function formatDateTime(epochMs: number): string {
  return dateFmt.format(new Date(epochMs));
}

export type FrCatalog = typeof fr;
